<template>
  <div class="dsd-root sc-page-root">
    <FiPageRail :title="isSelfServiceDriver ? 'My Scorecard' : 'Individual Driver'" nowrap-actions>
      <template #actions>
        <b-button v-if="individualDriver" size="sm" variant="outline-secondary"
                  @click="generatePdfReport()" :disabled="generating_pdf"
                  :title="generating_pdf ? 'Preparing PDF…' : 'Download PDF report'"
                  :aria-label="generating_pdf ? 'Preparing PDF report' : 'Download PDF report'">
          <i class="fa" :class="generating_pdf ? 'fa-spinner fa-spin' : 'fa-download'"></i>
        </b-button>
        <driver-manager-individual-help :self-service="isSelfServiceDriver" />
      </template>
    </FiPageRail>

    <!-- date preset chips beside always-visible pickers; every change reloads the
         data itself (app-wide auto-refresh doctrine), so there is no manual
         refresh button -->
    <div class="sc-toolbar">
      <div class="sc-toolbar-group dsd-date-presets" role="group" aria-label="Date range">
        <button v-for="m in quickRangeOptions" :key="'qr-' + m" type="button"
          :class="['sc-chip', { 'sc-chip--active': activeQuickRangeMonths === m }]"
          :aria-pressed="activeQuickRangeMonths === m ? 'true' : 'false'"
          @click="setDatePreset(m)">{{ m }}m</button>
      </div>
      <div class="dsd-date-fields">
        <date-picker class="sc-control dsd-date-field" v-model="details_fromDate" :config="optionsFrom" :placeholder="details_fromDate"/>
        <span class="dsd-date-sep">to</span>
        <date-picker class="sc-control dsd-date-field" v-model="details_toDate" :config="optionsTo" :placeholder="details_toDate"/>
      </div>
      <!-- FI-2116 polish: the driver picker is a toolbar control like its
           erm siblings, not a centered hero row of its own -->
      <div v-if="!isSelfServiceDriver" class="sc-toolbar-group">
        <span class="dsd-toolbar-label">Driver</span>
        <b-form-select v-model="selected_driver_id" :options="driverSelectOptions" class="sc-control sc-control-select dsd-driver-select"/>
      </div>
      <template v-if="!isCmTenant && !isSelfServiceDriver">
        <div class="sc-toolbar-group">
          <span class="dsd-toolbar-label">Status</span>
          <b-form-select v-model="selected_status_filter" :options="status_filter_options" class="sc-control sc-control-select"/>
        </div>
      </template>
    </div>

    <div v-if="loading && !drivers.length && !error" style="text-align: center; margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else-if="error" class="text-center text-danger" style="margin: 50px;">
      <p>{{ error }}</p>
      <b-button size="sm" variant="outline-secondary" @click="load_data()">Retry</b-button>
    </div>
    <div v-else :class="{ 'dsd-stale': loading }">
      <div v-if="!individualDriver" class="text-center text-muted" style="margin: 50px;">
        {{ isSelfServiceDriver ? 'No scorecard yet for this date range.' : 'Select a driver to view their KPI profile.' }}
      </div>
      <div v-else style="margin: 0 10px;">
        <div v-if="criticalFlagCount(individualDriver) > 0" class="alert alert-danger text-center" style="font-size: 0.9rem;">
          ⚠ {{ criticalFlagCount(individualDriver) }} critical flag{{ criticalFlagCount(individualDriver) > 1 ? 's' : '' }}<template v-if="!isSelfServiceDriver"> —
          <router-link v-if="canViewCoachingFlagged"
            :to="{ name: 'coaching_flagged', query: { driver_id: selected_driver_id, tenant: user.tenant } }"
          >view in Flagged Drivers</router-link>
          <router-link v-else :to="{ name: 'driver_manager_dashboard', query: { tab: 'flagged' } }">view in Flagged Drivers</router-link></template>
        </div>

        <b-card class="mb-3">
          <div class="dsd-individual-header">
            <!-- icon-only so it stays out of the way of the name/score at every
                 width: it used to go full-width above the driver name on mobile,
                 which made an export the first thing on the page -->
            <div class="dsd-individual-header-main">
              <h3 class="mb-0">{{ individualDriver.driver_name }}</h3>
              <div class="mt-1" style="font-size: 0.9rem;">
                <span :class="getCompiledTierBadgeClass(individualDriver.compiled_score_tier)">{{ individualDriver.compiled_score_tier }}</span>
                <span v-if="individualRank" class="text-muted ml-2">Rank #{{ individualRank }} of {{ rankedDriverCount }}</span>
                <!-- a driver gets no placing by design (see individualRank), which is
                     not the same thing as being unranked -- without this guard the
                     v-else told every driver their activity was insufficient -->
                <span v-else-if="!isSelfServiceDriver" class="text-muted ml-2">Not ranked: insufficient activity this period</span>
              </div>
              <div class="mt-1" style="font-size: 0.85rem;" v-if="!isCmTenant && (individualDriver.employment_status || individualDriver.date_employment_start)">
                <span v-if="individualDriver.employment_status" class="badge badge-secondary">{{ individualDriver.employment_status }}</span>
                <span v-if="individualDriver.date_employment_start" class="text-muted ml-2">
                  Hired {{ formatHireDate(individualDriver.date_employment_start) }}<span v-if="tenureString(individualDriver.date_employment_start)"> &bull; {{ tenureString(individualDriver.date_employment_start) }}</span>
                </span>
              </div>
              <div class="dsd-individual-score mt-3">
                <h5 class="mb-0">Combined Score</h5>
                <div class="compiled-score-badge" :class="getCompiledScoreClass(individualDriver.compiled_score, individualDriver.compiled_score_tier)">
                  <span class="compiled-score-number">{{ formatNumber(individualDriver.compiled_score) }}</span>
                  <span class="compiled-score-label">/ 100</span>
                </div>
              </div>
            </div>
          </div>
          <p class="text-muted mt-2 mb-0" style="font-size: 0.85rem;">{{ individualDriver.bus_days || 0 }} bus days &nbsp;·&nbsp; {{ formatNumber(individualDriver.total_miles) }} total miles</p>
          <driver-manager-score-breakdown
            :breakdown="individualDriver.compiled_score_breakdown"
            :driver="individualDriver"
          />
        </b-card>

        <div class="dsd-kpi-group" v-for="group in individualKpiGroups" :key="group.title" role="group" :aria-label="group.title">
          <h4 class="dsd-kpi-group-title">{{ group.title }}</h4>
          <div class="scorecard-metrics-row dsd-individual-metrics-row">
            <b-card bg-variant="light" class="metric-card metric-card-inline" :class="[k.trimClass, { 'dsd-kpi-wide': k.wide }]" v-for="k in group.kpis" :key="k.label">
              <h5>{{ k.label }}</h5>
              <h3>{{ k.display }}<span class="text-muted" style="font-size: 0.7rem;"> {{ k.unit }}</span></h3>
              <span class="scorecard-delta"><span v-if="k.deltaArrow" :class="k.deltaClass">{{ k.deltaArrow }}</span> {{ k.deltaRest }}</span>
              <span v-if="k.pctLabel" class="dsd-pct-pill" :class="k.pctClass">{{ k.pctLabel }}</span>
            </b-card>
          </div>
        </div>

        <div class="dsd-chart-grid dsd-two-col">
          <div class="ad-chart-card">
            <div class="ad-chart-title">Safety Profile vs Fleet Avg</div>
            <div class="ad-chart-canvas-wrap" style="height: 280px;"><canvas ref="chRadar"></canvas></div>
          </div>
          <div class="ad-chart-card">
            <div class="ad-chart-title">Activity vs Fleet Avg</div>
            <div class="ad-chart-canvas-wrap" style="height: 280px;"><canvas ref="chActivity"></canvas></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden PDF export area for the Download PDF Report button -->
    <div style="position: absolute; overflow: hidden; height: 0; width: 0;">
      <vue-html2pdf
        :enable-download="true"
        :manual-pagination="true"
        pdf-content-width="750px"
        :html-to-pdf-options="htmlToPdfOptions"
        @hasStartedGeneration="generating_pdf = true"
        @hasGenerated="generating_pdf = false"
        ref="scorecardPdf"
      >
        <section v-if="individualDriver" slot="pdf-content" class="pdf-item" style="font-family: Arial, sans-serif; color: #1a1a2e; padding: 0 10px;">

          <!-- PDF Header -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: #1a3a6b; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 10px;">
              <img src="/favicon-white.png" style="height: 36px; object-fit: contain;" alt="Fleet Integrations"/>
              <span style="font-size: 16px; font-weight: 700; color: #fff;">Fleet Integrations</span>
            </div>
            <div style="font-size: 18px; font-weight: 700; color: #fff;">Driver Manager Report</div>
          </div>

          <!-- Driver + period summary -->
          <div style="margin: 8px 0 8px 0; padding: 8px 12px; background: #f0f4fb; border-left: 5px solid #1a3a6b; border-radius: 3px; text-align: center;">
            <div style="font-size: 16px; font-weight: 700; color: #1a3a6b; margin-bottom: 2px;">{{ individualDriver.driver_name || 'Driver' }}</div>
            <div v-if="!isCmTenant && (individualDriver.employment_status || individualDriver.date_employment_start)" style="font-size: 12px; color: #444; margin-bottom: 2px;">
              <span v-if="individualDriver.employment_status"><strong>Status:</strong> {{ individualDriver.employment_status }}</span>
              <span v-if="individualDriver.employment_status && individualDriver.date_employment_start">&nbsp;&nbsp;|&nbsp;&nbsp;</span>
              <span v-if="individualDriver.date_employment_start"><strong>Hire Date:</strong> {{ formatHireDate(individualDriver.date_employment_start) }}<span v-if="tenureString(individualDriver.date_employment_start)"> ({{ tenureString(individualDriver.date_employment_start) }})</span></span>
            </div>
            <div style="font-size: 12px; color: #444;">
              {{ details_fromDate }} &mdash; {{ details_toDate }}
              &nbsp;&nbsp;<strong>Miles Driven:</strong> {{ formatNumber(individualDriver.miles_driven || individualDriver.total_miles, 2) }}
            </div>
          </div>

          <!-- Combined Score -->
          <div style="margin-top: 8px; padding: 8px 12px; background: #fff; border: 1px solid #d0d8e8; border-radius: 3px; text-align: center;">
            <span style="font-size: 12px; font-weight: 600; color: #555; text-transform: uppercase; letter-spacing: 0.4px;">Combined Score</span>
            <div style="font-size: 26px; font-weight: 700;" :style="{ color: compiledScoreColor(individualDriver.compiled_score, individualDriver.compiled_score_tier) }">{{ formatNumber(individualDriver.compiled_score) }} / 100 &nbsp;<span style="font-size: 14px; font-weight: 600;">({{ individualDriver.compiled_score_tier }})</span></div>
          </div>

          <!-- Safety Metrics section -->
          <div style="margin-top: 8px;">
            <div style="background: #1a3a6b; color: #fff; font-size: 11px; font-weight: 700; padding: 4px 10px; letter-spacing: 0.4px; text-transform: uppercase;">
              Safety Metrics
            </div>
            <table style="width: 100%; border-collapse: collapse; background: #fff;">
              <thead>
                <tr style="background: #e8edf5;">
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Safety Score</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Safety Rank</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Speeding %</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Distracted Driving per 10k</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Following Dist. per 10k</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Harsh Braking per 10k</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 18px; font-weight: 700;" :style="{ color: fieldColor('risk_score', individualDriver.risk_score) }">{{ individualDriver.risk_score || 'N/A' }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 18px; font-weight: 700;" :style="{ color: fieldColor('risk_rank', isActive(individualDriver) ? individualDriver.risk_score_rank : null, { total_drivers: rankedDriverCount }) }">{{ isActive(individualDriver) ? (individualDriver.risk_score_rank || 'N/A') : 'N/A' }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600;" :style="{ color: fieldColor('speeding_percentage', individualDriver.speeding_percentage) }">{{ formatNumber(individualDriver.speeding_percentage, 2) }}%</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600;" :style="{ color: fieldColor('distracted_driving_percentage', individualDriver.distracted_driving_per_10k_miles) }">{{ formatNumber(individualDriver.distracted_driving_per_10k_miles, 2) }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600;" :style="{ color: fieldColor('following_distance_per_10k_miles', individualDriver.following_distance_per_10k_miles) }">{{ formatNumber(individualDriver.following_distance_per_10k_miles, 2) }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600;" :style="{ color: fieldColor('harsh_braking_per_10k_miles', individualDriver.harsh_braking_per_10k_miles) }">{{ formatNumber(individualDriver.harsh_braking_per_10k_miles, 2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Idling & Fuel section -->
          <div style="margin-top: 8px;">
            <div style="background: #1a3a6b; color: #fff; font-size: 11px; font-weight: 700; padding: 4px 10px; letter-spacing: 0.4px; text-transform: uppercase;">
              Idling &amp; Fuel
            </div>
            <table style="width: 100%; border-collapse: collapse; background: #fff;">
              <thead>
                <tr style="background: #e8edf5;">
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Idling %</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Idling Rank</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">MPG</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Fuel (Gallons)</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Idle Hours</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Run Hours</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 700;" :style="{ color: fieldColor('idling_percentage', individualDriver.idling_percentage) }">{{ formatNumber(individualDriver.idling_percentage, 2) }}%</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 18px; font-weight: 700;" :style="{ color: fieldColor('idling_rank', isActive(individualDriver) ? individualDriver.idling_rank : null, { total_drivers: rankedDriverCount }) }">{{ isActive(individualDriver) ? (individualDriver.idling_rank || 'N/A') : 'N/A' }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 700;" :style="{ color: fieldColor('miles_per_gallon', individualDriver.miles_per_gallon) }">{{ formatNumber(individualDriver.miles_per_gallon, 2) || 'N/A' }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600;" :style="{ color: fieldColor('fuel_consumed_gallons', individualDriver.fuel_consumed_gallons) }">{{ formatNumber(individualDriver.fuel_consumed_gallons, 2) || '0.00' }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600;" :style="{ color: fieldColor('idle_hours', individualDriver.idle_hours) }">{{ formatNumber(individualDriver.idle_hours, 2) }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ formatNumber(individualDriver.run_hours, 2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- DVIR section -->
          <div style="margin-top: 8px;">
            <div style="background: #1a3a6b; color: #fff; font-size: 11px; font-weight: 700; padding: 4px 10px; letter-spacing: 0.4px; text-transform: uppercase;">
              DVIR (Vehicle Inspections)
            </div>
            <table style="width: 100%; border-collapse: collapse; background: #fff;">
              <thead>
                <tr style="background: #e8edf5;">
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">DVIRs / (Bus Days × 2)</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Pre-Trip DVIRs</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Post-Trip DVIRs</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Total DVIRs</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 18px; font-weight: 700;" :style="{ color: fieldColor('dvir_completion_rate', individualDriver.dvir_completion_rate) }">{{ formatNumber(individualDriver.dvir_completion_rate, 1) }}%</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ individualDriver.pre_trip_dvir_count || 0 }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ individualDriver.post_trip_dvir_count || 0 }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ individualDriver.total_dvir_count || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Dispatch section -->
          <div style="margin-top: 8px;">
            <div style="background: #1a3a6b; color: #fff; font-size: 11px; font-weight: 700; padding: 4px 10px; letter-spacing: 0.4px; text-transform: uppercase;">
              {{ isCmTenant ? 'Dispatch (from CM)' : 'Dispatch (from TBN)' }}
            </div>
            <table style="width: 100%; border-collapse: collapse; background: #fff;">
              <thead>
                <tr style="background: #e8edf5;">
                  <th v-if="!isCmTenant" style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Customer Requests</th>
                  <th v-if="!isCmTenant" style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Cust. Req. per 10k</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Bus Days</th>
                  <th v-if="!isCmTenant" style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Dispatch Miles</th>
                  <th v-if="!isCmTenant" style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Weekend Avail.</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Dispatch Count</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td v-if="!isCmTenant" style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 700;" :style="{ color: fieldColor('customer_requests', individualDriver.customer_requests || 0) }">{{ individualDriver.customer_requests || 0 }}</td>
                  <td v-if="!isCmTenant" style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 700;" :style="{ color: fieldColor('customer_requests_per_10k_miles', individualDriver.customer_requests_per_10k_miles || 0) }">{{ formatNumber(individualDriver.customer_requests_per_10k_miles, 2) }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600;" :style="{ color: fieldColor('bus_days', individualDriver.bus_days || 0) }">{{ individualDriver.bus_days || 0 }}</td>
                  <td v-if="!isCmTenant" style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ formatNumber(individualDriver.total_miles, 2) }}</td>
                  <td v-if="!isCmTenant" style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600;" :style="{ color: fieldColor('weekend_availability_percentage', individualDriver.weekend_availability_percentage) }">{{ formatNumber(individualDriver.weekend_availability_percentage, 2) }}%</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ individualDriver.dispatch_count || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- FI-2217 kudos section: ERM, not TBN, so not under Dispatch -->
          <div v-if="hasKudos" style="margin-top: 8px;">
            <div style="background: #1a3a6b; color: #fff; font-size: 11px; font-weight: 700; padding: 4px 10px; letter-spacing: 0.4px; text-transform: uppercase;">
              Kudos (from ERM)
            </div>
            <table style="width: 100%; border-collapse: collapse; background: #fff;">
              <thead>
                <tr style="background: #e8edf5;">
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Kudos</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Kudos per 10k</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ individualDriver.kudos == null ? '—' : individualDriver.kudos }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 700;" :style="{ color: fieldColor('kudos_per_10k_miles', individualDriver.kudos_per_10k_miles) }">{{ individualDriver.kudos_per_10k_miles == null ? '—' : formatNumber(individualDriver.kudos_per_10k_miles, 2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Color legend + footer -->
          <div style="margin-top: 10px; padding: 6px 12px; background: #f8f9fa; border: 1px solid #e0e0e0; border-radius: 3px; display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 10px; font-weight: 600; color: #555;">
              <span style="color: #16a34a;">&#9632;</span> Good &nbsp;
              <span style="color: #d97706;">&#9632;</span> Needs Attention &nbsp;
              <span style="color: #dc2626;">&#9632;</span> Poor
            </span>
            <span style="font-size: 10px; color: #888;">Fleet Integrations &mdash; Generated: {{ pdfGeneratedAt }}</span>
          </div>

        </section>
      </vue-html2pdf>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { Chart, registerables } from 'chart.js'
import { justDate } from '../helpers/dates'
import { get_all_driver_scorecards } from '../networking/tbn_apis'
import { cm_get_all_driver_scorecards } from '../networking/apis'
import { mergeThresholds, getFieldClass, getCompiledScoreClass, tierClass, getTierColor } from '../utils/driver_manager_thresholds'
import {
  QUICK_RANGE_MONTHS,
  activeQuickRangeMonths,
  defaultScorecardRange,
  normalizeRange,
  quickRangeDates,
  scorecardPickerOptions,
} from '../utils/driver_manager_dates'
import { isActiveDriver } from '../utils/scorecard_flags'
import dtPickerPortal from '../mixins/dtpicker_portal_mixin'
import VueHtml2pdf from 'vue-html2pdf'
import DriverManagerScoreBreakdown from './driver_manager_score_breakdown'
import { isFeatureEnabled } from '../utils/alerts_access'
import { tenantScorecardHasKudos } from '../utils/erm_access'

Chart.register(...registerables)

// Metrics checked for coaching flags -- see driver_manager_dashboard.vue's own
// copy of FLAG_METRICS for the full rationale; kept in sync manually since this
// page only needs it for the "N critical flags" banner + link to Flagged Drivers.
const FLAG_METRICS = [
  { thresholdKey: 'risk_score', dataKey: 'risk_score', label: 'Safety Score', dvirGate: false, higherBetter: true,
    note: (sev, v) => sev === 'critical' ? `Safety score ${Math.round(v)} is well below target.` : `Safety score ${Math.round(v)} is below target.` },
  { thresholdKey: 'dvir_completion_rate', dataKey: 'dvir_completion_rate', label: 'DVIR Completion', dvirGate: true, higherBetter: true,
    note: (sev, v) => sev === 'critical' ? `DVIR completion is only ${Math.round(v)}%.` : `DVIR completion is at ${Math.round(v)}%, below target.` },
  { thresholdKey: 'speeding_percentage', dataKey: 'speeding_percentage', label: 'Speeding', dvirGate: false, higherBetter: false,
    note: (sev, v) => sev === 'critical' ? `Speeding on ${Math.round(v)}% of miles, well above target.` : `Speeding on ${Math.round(v)}% of miles is above target.` },
  { thresholdKey: 'distracted_driving_percentage', dataKey: 'distracted_driving_per_10k_miles', label: 'Distracted Driving', dvirGate: false, higherBetter: false,
    note: (sev, v) => sev === 'critical' ? `Distracted driving at ${Math.round(v)}/10k miles, well above target.` : `Distracted driving at ${Math.round(v)}/10k miles is above target.` },
  { thresholdKey: 'following_distance_per_10k_miles', dataKey: 'following_distance_per_10k_miles', label: 'Following Distance', dvirGate: false, higherBetter: false,
    note: (sev, v) => `Following distance violations at ${Math.round(v)}/10k miles is above target.` },
  { thresholdKey: 'harsh_braking_per_10k_miles', dataKey: 'harsh_braking_per_10k_miles', label: 'Harsh Braking', dvirGate: false, higherBetter: false,
    note: (sev, v) => `Harsh braking at ${Math.round(v)}/10k miles is above target.` },
]
const FLAG_BADNESS_CRITICAL_PCT = 90
const FLAG_BADNESS_WARNING_PCT = 75

const RADAR_LABELS = ['Safety Score', 'No Speeding', 'No Distraction', 'Following Dist', 'Low Idling', 'MPG']

import DriverManagerIndividualHelp from './driver_manager_individual_help.vue'

export default {
  name: 'driver_manager_individual',

  mixins: [dtPickerPortal],

  components: { DriverManagerIndividualHelp, VueHtml2pdf, DriverManagerScoreBreakdown },

  data() {
    return {
      loading: false,
      generating_pdf: false,
      pdfFilename: '',
      pdfGeneratedAt: '',
      error: null,
      drivers: [],
      fleet_average: null,
      scorecard_thresholds: null,
      // set from the server payload when this user is a driver reading their own
      // scorecard: `drivers` then holds their row only, so fleet-relative figures
      // can't be derived from it
      self_service: false,
      self_service_driver_count: 0,
      selected_status_filter: null,
      selected_driver_id: null,
      details_fromDate: justDate(defaultScorecardRange().from),
      details_toDate: justDate(defaultScorecardRange().to),
      chartInstances: {},
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    mergedThresholds() {
      return mergeThresholds(this.scorecard_thresholds)
    },
    // FI-2110: the critical-flag banner deep-links into the standalone Flagged
    // Drivers page. That route's guard (src/router/index.js) is role list AND the
    // tenant flag with NO staff bypass, so this mirrors it exactly — otherwise the
    // link would bounce the user to the dashboard. Falls back to the Driver Manager
    // flagged tab, which every scorecard viewer can already reach.
    canViewCoachingFlagged() {
      const isCoachingRole = ['admin', 'customer-admin', 'customer', 'manager'].includes(this.user.role)
      const features = (this.user && this.user.features && this.user.features[this.user.tenant]) || {}
      return isCoachingRole && isFeatureEnabled(features.coaching_portal_enabled)
    },
    isCmTenant() {
      return (this.user && this.user.types && this.user.types[this.user.tenant]) === 'CM'
    },
    hasKudos() {
      return tenantScorecardHasKudos(this.user)
    },
    // a driver reading their own scorecard. the server decides this (it scopes the
    // payload); the role check just lets the page lay itself out correctly before
    // and during the first load.
    isSelfServiceDriver() {
      return (this.user && this.user.role === 'driver') || this.self_service
    },
    status_filter_options() {
      const statuses = [...new Set(this.drivers.map(d => d.employment_status).filter(s => s && s.trim()))].sort()
      return [
        { value: null, text: 'All Statuses' },
        ...statuses.map(s => ({ value: s, text: s })),
      ]
    },
    statusFilteredDrivers() {
      if (!this.selected_status_filter) return this.drivers
      return this.drivers.filter(d => d.employment_status === this.selected_status_filter)
    },
    activeDrivers() {
      return this.drivers.filter(d => this.isActive(d))
    },
    scoreRanked() {
      return [...this.activeDrivers].sort((a, b) => (b.compiled_score || 0) - (a.compiled_score || 0))
    },
    driverSelectOptions() {
      const sorted = [...this.statusFilteredDrivers].sort((a, b) => (a.driver_name || '').localeCompare(b.driver_name || ''))
      return [
        { value: null, text: 'Select Driver' },
        ...sorted.map(d => ({ value: d.driver_id || d.driver_name, text: d.driver_name })),
      ]
    },
    individualDriver() {
      if (!this.selected_driver_id) return null
      return this.drivers.find(d => String(d.driver_id) === String(this.selected_driver_id) || d.driver_name === this.selected_driver_id) || null
    },
    individualRank() {
      if (!this.individualDriver || !this.isActive(this.individualDriver)) return null
      // one row in, one row ranked: "Rank #1 of 1" would be a flattering lie, so a
      // driver gets no placing at all. their own KPIs against the fleet average is
      // the comparison this page offers them.
      if (this.isSelfServiceDriver) return null
      return this.scoreRanked.findIndex(d => d === this.individualDriver) + 1
    },
    rankedDriverCount() {
      // the per-KPI rank colouring scales against fleet size; the server sends the
      // real count alongside the single scoped row
      if (this.isSelfServiceDriver) return this.self_service_driver_count || 0
      return this.scoreRanked.length
    },
    individualKpiGroups() {
      if (!this.individualDriver) return []
      const d = this.individualDriver
      const fa = this.fleet_average || {}
      const TRIM_CLASS = {
        'text-success': 'dsd-kpi-trim-success',
        'text-info': 'dsd-kpi-trim-info',
        'text-primary': 'dsd-kpi-trim-primary',
        'text-warning': 'dsd-kpi-trim-warning',
        'text-danger': 'dsd-kpi-trim-danger',
      }
      const kpi = (label, value, unit, higherBetter, thresholdKey, dataKey, context) => {
        const display = value == null ? '—' : this.formatNumber(value)
        const valueClass = thresholdKey ? this.fieldClass(thresholdKey, value, context) : ''
        const trimClass = TRIM_CLASS[valueClass] || ''
        const faVal = fa[dataKey]
        let deltaArrow = '', deltaRest = '—', deltaClass = ''
        if (value != null && faVal != null && faVal !== 0) {
          const diff = value - faVal
          const pct = Math.abs((diff / faVal) * 100)
          if (Math.abs(diff) < 0.001) {
            deltaRest = '= fleet avg'
          } else {
            const improved = higherBetter ? diff > 0 : diff < 0
            deltaClass = improved ? 'delta-good' : 'delta-bad'
            deltaArrow = diff > 0 ? '▲' : '▼'
            deltaRest = `${this.formatNumber(pct)}% vs avg`
          }
        }
        const pct = thresholdKey ? this.percentile(dataKey, higherBetter) : null
        let pctLabel = null, pctClass = ''
        if (pct != null) {
          pctClass = pct >= 70 ? 'dsd-pct-top' : pct >= 35 ? 'dsd-pct-mid' : 'dsd-pct-bot'
          pctLabel = pct >= 70 ? `Top ${100 - pct}%` : pct >= 35 ? `${pct}th pctile` : `Bottom ${pct}%`
        }
        return { label, display, unit, deltaArrow, deltaRest, deltaClass, pctLabel, pctClass, valueClass, trimClass }
      }
      const rankCtx = { total_drivers: this.rankedDriverCount }
      // Section titles/order mirror the Compare page's metric_rows sections and the
      // PDF export below, so the same metrics read the same way on all three surfaces.
      const groups = [
        {
          title: 'Safety Metrics',
          kpis: [
            kpi('Safety Score', d.risk_score, '/100', true, 'risk_score', 'risk_score'),
            kpi('Safety Rank', this.isActive(d) ? d.risk_score_rank : null, '', false, 'risk_rank', 'risk_score_rank', rankCtx),
            kpi('Speeding', d.speeding_percentage, '%', false, 'speeding_percentage', 'speeding_percentage'),
            kpi('Distracted Driving', d.distracted_driving_per_10k_miles, '/10k mi', false, 'distracted_driving_percentage', 'distracted_driving_per_10k_miles'),
            kpi('Following Distance', d.following_distance_per_10k_miles, '/10k mi', false, 'following_distance_per_10k_miles', 'following_distance_per_10k_miles'),
            kpi('Harsh Braking', d.harsh_braking_per_10k_miles, '/10k mi', false, 'harsh_braking_per_10k_miles', 'harsh_braking_per_10k_miles'),
          ],
        },
        {
          title: 'Idling & Fuel',
          kpis: [
            kpi('Idling', d.idling_percentage, '%', false, 'idling_percentage', 'idling_percentage'),
            kpi('Idling Rank', this.isActive(d) ? d.idling_rank : null, '', false, 'idling_rank', 'idling_rank', rankCtx),
            kpi('Miles Per Gallon', d.miles_per_gallon, 'MPG', true, 'miles_per_gallon', 'miles_per_gallon'),
            kpi('Fuel', d.fuel_consumed_gallons, 'gal', false, 'fuel_consumed_gallons', 'fuel_consumed_gallons'),
            kpi('Idle Hours', d.idle_hours, 'hrs', false, 'idle_hours', 'idle_hours'),
            kpi('Run Hours', d.run_hours, 'hrs', true, null, 'run_hours'),
          ],
        },
        {
          title: 'DVIR (Vehicle Inspections)',
          kpis: [
            // three tiles in this group, so one is always alone on a 2-across
            // row. DVIRs / (Bus Days × 2) is the scored metric here (the two counts
            // carry no threshold), so it leads full-width and the raw pre/post
            // counts pair up beneath it, rather than Post-Trip dangling.
            { ...kpi('DVIRs / (Bus Days × 2)', d.dvir_completion_rate, '%', true, 'dvir_completion_rate', 'dvir_completion_rate'), wide: true },
            kpi('Pre-Trip DVIRs', d.pre_trip_dvir_count, '', true, null, 'pre_trip_dvir_count'),
            kpi('Post-Trip DVIRs', d.post_trip_dvir_count, '', true, null, 'post_trip_dvir_count'),
          ],
        },
      ]
      // CM tenants have no availability/customer-request feed, so their Dispatch
      // section is just the three activity tiles.
      const dispatchKpis = []
      if (!this.isCmTenant) {
        dispatchKpis.push(
          kpi('Weekend Availability', d.weekend_availability_percentage, '%', true, 'weekend_availability_percentage', 'weekend_availability_percentage'),
          kpi('Customer Requests', d.customer_requests, '', true, 'customer_requests', 'customer_requests'),
          kpi('Cust. Req. / 10k', d.customer_requests_per_10k_miles, '', true, 'customer_requests_per_10k_miles', 'customer_requests_per_10k_miles'),
        )
      }
      dispatchKpis.push(
        kpi('Total Miles', d.total_miles, 'mi', true, null, 'total_miles'),
        kpi('Bus Days', d.bus_days, 'days', true, 'bus_days', 'bus_days'),
        kpi('Dispatch Count', d.dispatch_count, '', true, null, 'dispatch_count'),
      )
      groups.push({
        title: this.isCmTenant ? 'Dispatch (CM)' : 'Dispatch (TBN)',
        kpis: dispatchKpis,
      })
      // FI-2217: customer kudos from the Event Response Manager, counted per
      // driver at read time. its own group because it isn't a TBN dispatch field.
      if (this.hasKudos) {
        groups.push({
          title: 'Kudos (ERM)',
          kpis: [
            kpi('Kudos', d.kudos, '', true, null, 'kudos'),
            kpi('Kudos / 10k', d.kudos_per_10k_miles, '', true, 'kudos_per_10k_miles', 'kudos_per_10k_miles'),
          ],
        })
      }
      return groups
    },
    quickRangeOptions() {
      return QUICK_RANGE_MONTHS
    },
    activeQuickRangeMonths() {
      return activeQuickRangeMonths(
        { from: this.details_fromDate, to: this.details_toDate })
    },
    htmlToPdfOptions() {
      return {
        margin: [12, 10, 12, 10],
        filename: this.pdfFilename + '.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        enableLinks: false,
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
      }
    },
    optionsFrom() {
      return scorecardPickerOptions('from')
    },
    optionsTo() {
      return scorecardPickerOptions('to')
    },
  },

  watch: {
    selected_driver_id(id) {
      this.syncDriverQuery(id)
      this.$nextTick(() => this.buildIndividualCharts())
    },
    '$route.query.driver_id'() {
      this.hydrateDriverFromRoute()
    },
    details_toDate() {
      const fixed = normalizeRange(
        { from: this.details_fromDate, to: this.details_toDate },
        { changed: 'to' })
      // null = unparseable (cleared or half-typed): leave it alone and do not
      // reload, or "Invalid date" reaches the api as a month string
      if (!fixed) return
      if (fixed.from !== this.details_fromDate) this.details_fromDate = fixed.from
      if (fixed.to !== this.details_toDate) this.details_toDate = fixed.to
      this.scheduleRangeLoad()
    },
    details_fromDate() {
      const fixed = normalizeRange(
        { from: this.details_fromDate, to: this.details_toDate },
        { changed: 'from' })
      // null = unparseable (cleared or half-typed): leave it alone and do not
      // reload, or "Invalid date" reaches the api as a month string
      if (!fixed) return
      if (fixed.from !== this.details_fromDate) this.details_fromDate = fixed.from
      if (fixed.to !== this.details_toDate) this.details_toDate = fixed.to
      this.scheduleRangeLoad()
    },
  },

  created() {
    this.hydrateDriverFromRoute()
    this.load_data()
  },

  beforeDestroy() {
    Object.values(this.chartInstances).forEach(c => c && c.destroy())
  },

  methods: {
    hydrateDriverFromRoute() {
      const id = this.$route.query.driver_id
      if (id && id !== this.selected_driver_id) this.selected_driver_id = id
    },
    syncDriverQuery(id) {
      const query = { ...this.$route.query }
      if (!id) delete query.driver_id
      else query.driver_id = id
      this.$router.replace({ query }).catch(() => {})
    },

    async load_data() {
      const seq = (this._loadSeq = (this._loadSeq || 0) + 1)
      this.loading = true
      this.error = null
      try {
        const start_month = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const end_month = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const response = this.isCmTenant
          ? await cm_get_all_driver_scorecards(this.user.tenant, start_month, end_month)
          : await get_all_driver_scorecards(this.user.tenant, start_month, end_month)
        if (seq !== this._loadSeq) return
        const responseData = (response && response.value && response.value.data) || (response && response.data)
        if (responseData && responseData.error) {
          this.error = responseData.error
          this.drivers = []
        } else if (responseData) {
          this.drivers = (responseData.drivers || []).map(d => this.normalizeDriverRow(d))
          this.fleet_average = this.normalizeDriverRow(responseData.fleet_average) || null
          this.scorecard_thresholds = responseData.scorecard_thresholds !== undefined ? responseData.scorecard_thresholds : null
          this.self_service = !!responseData.self_service
          this.self_service_driver_count = responseData.ranked_driver_count || 0
          // a driver never picks: select the single row the server allowed through,
          // whatever ?driver_id said
          if (this.isSelfServiceDriver) {
            this.selected_driver_id = this.drivers.length ? this.drivers[0].driver_id : null
          }
        } else {
          this.error = 'Unexpected response format from server'
        }
      } catch (e) {
        if (seq !== this._loadSeq) return
        const serverMsg = (e.response && e.response.data && e.response.data.error)
          || (e.response && e.response.data && e.response.data.data && e.response.data.data.error)
        this.error = serverMsg || e.message || 'Error loading driver data'
      } finally {
        if (seq === this._loadSeq) {
          this.loading = false
          this.$nextTick(() => this.buildIndividualCharts())
        }
      }
    },


    // a chip writes its range into the pickers, which are always visible and
    // show exactly the days the page reads
    setDatePreset(months) {
      const { from, to } = quickRangeDates(months)
      this.details_fromDate = from
      this.details_toDate = to
    },
    scheduleRangeLoad() {
      if (this._rangeLoadScheduled) return
      this._rangeLoadScheduled = true
      this.$nextTick(() => {
        this._rangeLoadScheduled = false
        this.load_data()
      })
    },

    // FI-2140: was a third copy of the activity gate and drifted from the other
    // two the moment the bar started scaling with the window. Delegates now, so
    // the roster, the Flagged tab and this page cannot disagree about who counts.
    isActive(d) {
      return isActiveDriver(d)
    },

    round2(value) {
      if (value === null || value === undefined || isNaN(Number(value))) return value
      return Math.round(Number(value) * 100) / 100
    },

    normalizeDriverRow(row) {
      if (!row) return row
      if (this.isCmTenant && row.total_miles == null && row.miles_driven != null) {
        return { ...row, total_miles: row.miles_driven }
      }
      return row
    },

    percentile(key, higherBetter) {
      if (!this.individualDriver) return null
      const valid = [...this.activeDrivers].filter(d => d[key] != null).sort((a, b) => a[key] - b[key])
      const idx = valid.findIndex(d => d === this.individualDriver)
      if (idx === -1 || valid.length < 2) return null
      const p = Math.round((idx / (valid.length - 1)) * 100)
      return higherBetter ? p : 100 - p
    },

    getCompiledScoreClass(score, tier) {
      return getCompiledScoreClass(score, this.mergedThresholds.compiled_score, tier)
    },

    getCompiledTierBadgeClass(tier) {
      return tierClass(tier, 'chip')
    },

    compiledScoreColor(score, tier) {
      const cls = this.getCompiledScoreClass(score, tier)
      const map = {
        'compiled-score-elite': '#16a34a',
        'compiled-score-solid': '#2563eb',
        'compiled-score-coaching': '#d97706',
        'compiled-score-highrisk': '#dc2626',
      }
      return map[cls] || '#1a1a2e'
    },

    fieldClass(key, value, context) {
      return getFieldClass(key, value, this.mergedThresholds, context)
    },

    fieldColor(key, value, context) {
      const cls = this.fieldClass(key, value, context)
      const map = {
        'text-success': '#16a34a',
        'text-info': '#0dcaf0',
        'text-primary': '#2563eb',
        'text-warning': '#d97706',
        'text-danger': '#dc2626',
      }
      return map[cls] || '#1a1a2e'
    },

    formatNumber(value, decimals = 2) {
      if (value === null || value === undefined || isNaN(Number(value))) return '—'
      const fixed = Number(value).toFixed(decimals)
      const allZeroDecimals = decimals > 0 && fixed.endsWith('.' + '0'.repeat(decimals))
      return allZeroDecimals ? fixed.slice(0, -(decimals + 1)) : fixed
    },

    formatHireDate(dateStr) {
      if (!dateStr) return ''
      const m = moment(dateStr, ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss'], true)
      if (m.isValid()) return m.format('MMM D, YYYY')
      const mFallback = moment(dateStr)
      return mFallback.isValid() ? mFallback.format('MMM D, YYYY') : dateStr
    },

    tenureString(dateStr) {
      if (!dateStr) return ''
      let start = moment(dateStr, ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss'], true)
      if (!start.isValid()) start = moment(dateStr)
      if (!start.isValid()) return ''
      const now = moment()
      const years = now.diff(start, 'years')
      const months = now.diff(start.clone().add(years, 'years'), 'months')
      if (years === 0 && months === 0) return 'Less than 1 month'
      const parts = []
      if (years > 0) parts.push(`${years} yr${years !== 1 ? 's' : ''}`)
      if (months > 0) parts.push(`${months} mo`)
      return parts.join(', ')
    },

    generatePdfReport() {
      if (!this.individualDriver || this.generating_pdf) return
      this.generating_pdf = true
      const name = this.individualDriver.driver_name || 'Driver'
      this.pdfFilename = `${name} ${this.details_fromDate} to ${this.details_toDate}`
      this.pdfGeneratedAt = moment().format('MMM D, YYYY h:mm A')
      this.$nextTick(() => {
        try {
          this.$refs.scorecardPdf.generatePdf()
        } catch (e) {
          this.generating_pdf = false
          console.error('PDF generation failed to start:', e)
        }
      })
    },

    metricBadnessPercentile(d, dataKey, higherBetter) {
      const value = d[dataKey]
      if (value == null) return null
      const sorted = this.activeDrivers.map(x => x[dataKey]).filter(v => v != null).sort((a, b) => a - b)
      if (!sorted || sorted.length < 2) return null
      let lo = 0
      let hiBound = sorted.length
      while (lo < hiBound) {
        const mid = (lo + hiBound) >> 1
        if (sorted[mid] < value) lo = mid + 1
        else hiBound = mid
      }
      const first = lo
      if (first >= sorted.length || sorted[first] !== value) return null
      let hi = first
      while (hi + 1 < sorted.length && sorted[hi + 1] === value) hi++
      const idx = (first + hi) / 2
      const p = (idx / (sorted.length - 1)) * 100
      return higherBetter ? 100 - p : p
    },

    getFlags(d) {
      if (!this.isActive(d)) return []
      const flags = []
      FLAG_METRICS.forEach(m => {
        const value = d[m.dataKey]
        if (value == null) return
        if (m.dvirGate && !d.total_dvir_count) return
        const fieldClass = getFieldClass(m.thresholdKey, value, this.mergedThresholds)
        if (fieldClass !== 'text-warning' && fieldClass !== 'text-danger') return
        const badnessPct = this.metricBadnessPercentile(d, m.dataKey, m.higherBetter)
        if (badnessPct == null) return
        if (fieldClass === 'text-danger' && badnessPct >= FLAG_BADNESS_CRITICAL_PCT) {
          flags.push({ key: m.dataKey, label: m.label, severity: 'critical', note: m.note('critical', value) })
        } else if (badnessPct >= FLAG_BADNESS_WARNING_PCT) {
          flags.push({ key: m.dataKey, label: m.label, severity: 'warning', note: m.note('warning', value) })
        }
      })
      return flags.sort((a, b) => (a.severity === 'critical' && b.severity !== 'critical') ? -1 : 1)
    },

    criticalFlagCount(d) {
      return this.getFlags(d).filter(f => f.severity === 'critical').length
    },

    applyChartTheme() {
      const midnight = document.documentElement.getAttribute('data-fi-theme') === 'midnight'
      this._midnight = midnight
      const rootStyles = getComputedStyle(document.documentElement)
      const mutedInk = rootStyles.getPropertyValue('--fi-text-muted').trim()
      const gridLine = rootStyles.getPropertyValue('--fi-line').trim()
      Chart.defaults.color = mutedInk || (midnight ? '#c7d2dd' : '#666')
      Chart.defaults.borderColor = gridLine || (midnight ? 'rgba(255, 255, 255, 0.09)' : 'rgba(0, 0, 0, 0.1)')
      Chart.defaults.font.family = getComputedStyle(document.body).fontFamily || Chart.defaults.font.family
    },

    tc(tier) {
      return getTierColor(tier, this._midnight ? 'midnight' : 'light')
    },

    surnameOf(name) {
      const n = (name || '').trim()
      if (!n) return ''
      const comma = n.indexOf(',')
      if (comma > 0) return n.slice(0, comma).trim()
      return n.split(' ').pop()
    },

    destroyChart(key) {
      if (this.chartInstances[key]) {
        this.chartInstances[key].destroy()
        this.chartInstances[key] = null
      }
    },

    freshCanvasCtx(canvasEl) {
      const existing = Chart.getChart(canvasEl)
      if (existing) existing.destroy()
      return canvasEl.getContext('2d')
    },

    radarMetricValues(row) {
      const r = row || {}
      const rn = v => Math.max(0, 100 - ((v || 0) / 3) * 100)
      const r2 = this.round2
      return [
        r2(r.risk_score),
        r2(rn(r.speeding_percentage)),
        r2(Math.max(0, 100 - ((r.distracted_driving_per_10k_miles || 0) / 15) * 100)),
        r2(Math.max(0, 100 - ((r.following_distance_per_10k_miles || 0) / 10) * 100)),
        r2(Math.max(0, 100 - ((r.idling_percentage || 0) / 60) * 100)),
        r2(Math.min(((r.miles_per_gallon || 0) / 10) * 100, 100)),
      ]
    },

    buildIndividualCharts() {
      const d = this.individualDriver
      if (!d || !this.$refs.chRadar) return
      try {
        this.buildIndividualChartsInner(d)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[driver_manager_individual] failed to build charts', e)
      }
    },

    buildIndividualChartsInner(d) {
      this.applyChartTheme()
      const fa = this.fleet_average || {}
      const color = this.tc(d.compiled_score_tier)
      const r2 = this.round2

      this.destroyChart('radar')
      this.chartInstances.radar = new Chart(this.freshCanvasCtx(this.$refs.chRadar), {
        type: 'radar',
        data: {
          labels: RADAR_LABELS,
          datasets: [
            {
              label: this.surnameOf(d.driver_name),
              data: this.radarMetricValues(d),
              backgroundColor: color + '33', borderColor: color, pointBackgroundColor: color, borderWidth: 2,
            },
            {
              label: 'Fleet Avg',
              data: this.radarMetricValues(fa),
              backgroundColor: 'rgba(108,117,125,.12)', borderColor: '#6c757d', pointBackgroundColor: '#6c757d',
              borderWidth: 1.5, borderDash: [4, 4],
            },
          ],
        },
        options: { maintainAspectRatio: false, scales: { r: { min: 0, max: 100, ticks: { display: false } } }, plugins: { legend: { position: 'bottom' } } },
      })

      this.destroyChart('activity')
      this.chartInstances.activity = new Chart(this.freshCanvasCtx(this.$refs.chActivity), {
        type: 'bar',
        data: {
          labels: ['Run Hours', 'Idle Hours', 'Bus Days', 'DVIRs', 'Miles÷100'],
          datasets: [
            {
              label: this.surnameOf(d.driver_name),
              backgroundColor: color, borderRadius: 4,
              data: [r2(d.run_hours), r2(d.idle_hours), r2(d.bus_days), d.total_dvir_count || 0, r2((d.total_miles || 0) / 100)],
            },
            {
              label: 'Fleet Avg',
              backgroundColor: '#adb5bd', borderRadius: 4,
              data: [r2(fa.run_hours), r2(fa.idle_hours), r2(fa.bus_days), fa.total_dvir_count || 0, r2((fa.total_miles || 0) / 100)],
            },
          ],
        },
        options: { maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } },
      })
    },
  },
}
</script>

<style scoped>
/* date preset chips (od-chip recipe, token-native so midnight needs no
   overrides) -- kept
   local since driver_manager_dashboard.vue's copy is scoped to that component */
.sc-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
/* :not(.sc-chip--active) is load-bearing: .sc-chip:hover out-specifies
   .sc-chip--active (a pseudo-class beats nothing at the same class count), so a
   bare hover rule repainted the SELECTED chip near-white while its own
   color: #fff survived -- white on white for as long as the pointer stayed on
   the chip you just clicked, in every theme. */
.sc-chip:not(.sc-chip--active):hover {
  background: var(--fi-surface-sunken, #eef4fb);
}
.sc-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
.sc-chip--active:hover {
  background: var(--fi-ops-accent-strong, #305b80);
  border-color: var(--fi-ops-accent-strong, #305b80);
}
.dsd-date-presets {
  gap: 6px;
}
.dsd-date-fields {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dsd-date-field {
  width: 122px;
  font-size: 0.9rem;
}
@media (max-width: 768px) {
  .dsd-date-field {
    font-size: 16px;
  }
}
.dsd-date-sep,
.dsd-toolbar-label {
  color: var(--fi-text-muted, #6c757d);
  font-size: 0.85rem;
}
.dsd-stale {
  opacity: 0.55;
  transition: opacity 0.15s;
}

/* base KPI-tile layout: left behind in driver_manager_dashboard.vue's scoped
   style when Individual Driver was split into this standalone page (FI-2064)
   -- restored here so .metric-card-inline actually lays out as a row instead
   of stacking full-width block cards. */
/* the group owns the spacing between sections; the tile row inside it sheds the
   bottom margin it carries when used standalone, so five stacked sections don't
   double up their gaps */
.dsd-kpi-group {
  margin-bottom: 1.5rem;
}
.dsd-kpi-group .scorecard-metrics-row {
  margin-bottom: 0;
}
.dsd-kpi-group-title {
  text-align: center;
  font-weight: bold;
  font-size: 0.95rem;
  color: var(--fi-text, #545454);
  margin-bottom: 0.5rem;
}
.scorecard-metrics-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
}
.metric-card-inline {
  flex: 0 0 auto;
  min-width: 128px;
  padding: 0.6rem 0.7rem !important;
  margin-bottom: 0 !important;
  text-align: center;
}
.metric-card-inline h5 {
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
  line-height: 1.15;
  color: #545454;
}
.metric-card-inline h2 {
  font-size: 1.35rem;
  margin-bottom: 0.1rem;
}
.metric-card-inline h3 {
  font-size: 1.15rem;
  margin-bottom: 0.1rem;
}
.metric-card-inline span {
  font-size: 0.7rem !important;
}

.dsd-individual-metrics-row .metric-card-inline.dsd-kpi-trim-success { border-left: 4px solid #28a745 !important; }
.dsd-individual-metrics-row .metric-card-inline.dsd-kpi-trim-info    { border-left: 4px solid #17a2b8 !important; }
.dsd-individual-metrics-row .metric-card-inline.dsd-kpi-trim-primary { border-left: 4px solid #007bff !important; }
.dsd-individual-metrics-row .metric-card-inline.dsd-kpi-trim-warning { border-left: 4px solid #d97706 !important; }
.dsd-individual-metrics-row .metric-card-inline.dsd-kpi-trim-danger  { border-left: 4px solid #dc3545 !important; }

.dsd-pct-pill {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  margin-top: 4px;
}
.dsd-pct-top { background: #d4edda; color: #155724; }
.dsd-pct-mid { background: #fff3cd; color: #856404; }
.dsd-pct-bot { background: #f8d7da; color: #721c24; }

.compiled-score-badge {
  display: inline-flex;
  align-items: baseline;
  padding: 6px 14px;
  border-radius: 8px;
  font-weight: bold;
  border: 2px solid var(--sc-score-badge-border, transparent);
}
.compiled-score-number { font-size: 2rem; line-height: 1; }
.compiled-score-label { font-size: 0.85rem; margin-left: 4px; opacity: 0.7; }

.dsd-chart-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 20px;
  margin-top: 20px;
}
.dsd-chart-grid.dsd-two-col {
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
}

.ad-chart-card {
  border: solid 1px var(--fi-line, #ddd);
  background: var(--fi-surface, #fff);
  border-radius: 12px;
  padding: 15px;
}
.ad-chart-title {
  text-align: center;
  font-weight: bold;
  font-size: 1rem;
  color: var(--fi-text, #545454);
  margin-bottom: 10px;
}
.ad-chart-canvas-wrap {
  position: relative;
  width: 100%;
}

.scorecard-delta {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  margin-top: 2px;
  color: #6c757d;
}
.delta-good    { color: #28a745; }
.delta-bad     { color: #dc3545; }

.dsd-individual-header {
  position: relative;
  display: flex;
  justify-content: center;
}
.dsd-individual-header-main {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 100%;
}
/* erm-style toolbar: left-aligned, compact, one voice for every control
   (the shared .sc-toolbar centers; this page opts out) */
.dsd-root .sc-toolbar { justify-content: flex-start; gap: 8px 6px; }
.dsd-root .sc-toolbar-group { justify-content: flex-start; }
.dsd-root .sc-control.sc-control-select { font-size: 14px; font-weight: 600; }
.dsd-root .dsd-driver-select { width: 230px; }

@media (max-width: 768px) {
  /* erm-style mobile: each labeled control is a tight full-width row.
     selects stay 16px here -- ios safari zooms focused controls below that */
  .dsd-root .sc-toolbar { gap: 6px; margin-bottom: 10px; }
  .dsd-root .sc-toolbar-group { width: 100%; }
  .dsd-root .sc-control.sc-control-select { flex: 1 1 auto; font-size: 16px; }
  .dsd-driver-select {
    width: 100% !important;
    max-width: 100% !important;
  }
  /* the radar chart stretches to the full-width single-column card on mobile
     while staying a fixed 280px tall; that extreme width-vs-height ratio makes
     Chart.js's asymmetric point-label padding (e.g. "Low Idling" is wider than
     "Safety Score") visibly push the hexagon off to one side. capping the width
     and centering it keeps the chart roughly square, like it is in the 2-col
     desktop layout, so it renders centered. */
  .ad-chart-canvas-wrap {
    max-width: 340px;
    margin: 0 auto;
  }
  /* the PDF action is an icon now, so it stays pinned upper-right at every
     width -- it no longer needs the column-reverse stack that used to put a
     full-width export button above the driver's name */
  /* the name shares its line with that icon and with the 30px gutters that keep
     clear of it, which costs 60px of an already narrow header. drop it from the
     bootstrap 1.75rem default so a long canonical TBN name ("LAST, FIRST")
     still lands on one line at 375px. */
  .dsd-individual-header-main h3 {
    font-size: 1.4rem;
  }
  .dsd-kpi-group {
    margin-bottom: 0.75rem;
  }
  .dsd-kpi-group-title {
    font-size: 0.8rem !important;
    margin-bottom: 0.3rem !important;
  }
  .dsd-individual-metrics-row {
    gap: 0.4rem !important;
    margin-bottom: 0.5rem !important;
  }
  /* FI-2114: two across, not three. the old 3-across fit 21 tiles by crushing
     labels to 0.6rem and the delta/percentile lines to 0.55rem (~8.8px), which
     is what read as "noisy" -- there was more type on the page than anyone
     could focus on. this beats BOTH shared rules in driver-manager-mobile.css
     (1 1 45% at <=768, 1 1 100% at <=420) on specificity: a full-width tile
     for a short label and one number would make the page 21 screens long. */
  .dsd-individual-metrics-row .metric-card-inline {
    flex: 1 1 calc(50% - 0.25rem) !important;
    min-width: 0 !important;
    padding: 0 !important;
  }
  /* a group's lead metric takes the full row, so an odd-numbered group leaves
     its supporting tiles paired instead of one stranded at the bottom */
  .dsd-individual-metrics-row .metric-card-inline.dsd-kpi-wide {
    flex: 1 1 100% !important;
  }
  .dsd-individual-metrics-row .metric-card-inline ::v-deep .card-body {
    padding: 0.5rem 0.45rem !important;
  }
  .dsd-individual-metrics-row .metric-card-inline h5 {
    font-size: 0.72rem !important;
    margin-bottom: 0.15rem !important;
    line-height: 1.2 !important;
    white-space: normal;
  }
  .dsd-individual-metrics-row .metric-card-inline h3 {
    font-size: 1.1rem !important;
    margin-bottom: 0 !important;
    line-height: 1.2 !important;
  }
  /* FI-2114: the trim border already says good/bad, so the delta line and the
     percentile pill were a second and third encoding of the same signal --
     ~40 of the page's ~84 text elements, none of them legible at 0.55rem.
     both stay on desktop, where there is room for them. */
  .dsd-individual-metrics-row .scorecard-delta,
  .dsd-individual-metrics-row .dsd-pct-pill {
    display: none !important;
  }
  .compiled-score-number {
    font-size: 1.8rem !important;
  }
  .compiled-score-badge {
    padding: 5px 12px !important;
  }
}
</style>
